import { RecommendationDataType } from '../../types/company.type';
import './RecommendationTable.css';
import { getFlagSvg } from '../Common/Flag/Flag';
import { formatNumber } from '../../utils/utils';
import CommonTooltip from '../Common/CommonTooltip/CommonTooltip';
import { useRef, useState } from 'react';
import { CommonTooltipType } from '../../types/common.type';
import classNames from 'classnames';

export type RecommendationTableProps = {
  data: RecommendationDataType[];
  page: number;
  size: number;
  isSmallScreen?: boolean;
  isLoading?: boolean;
  onStockClick?: (symbol: string) => void;
};

const RecommendationTable = ({
  isSmallScreen,
  data,
  page,
  size,
  isLoading,
  onStockClick,
}: RecommendationTableProps) => {
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [tooltip, setTooltip] = useState<CommonTooltipType>({
    content: '',
    show: false,
    x: 0,
    y: 0,
  });

  const getRecommendationClass = (recommendation: string) => {
    switch (recommendation) {
      case 'Strong Buy':
        return 'rec-strong-buy';
      case 'Buy':
        return 'rec-buy';
      case 'Hold':
        return 'rec-hold';
      case 'Reduce':
        return 'rec-reduce';
      case 'Sell':
        return 'rec-sell';
      default:
        return 'rec-hold';
    }
  };

  const constrainTooltipPosition = (e: React.MouseEvent) => {
    // Estimate tooltip dimensions (these are approximate values from CSS)
    // Calculate initial position based on item position and mouse offset
    let x = e.pageX + 10;
    let y = e.pageY;

    const elementMaxWidth = tooltipRef.current?.clientWidth || 0;
    const elementMaxHeight = tooltipRef.current?.clientHeight || 0;
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;
    const viewportWidth = window.innerWidth + scrollX;
    const viewportHeight = window.innerHeight + scrollY;

    if (x + elementMaxWidth > viewportWidth) {
      x = x - elementMaxWidth;
    }
    if (y + elementMaxHeight > viewportHeight) {
      y = y - elementMaxHeight;
    }

    return { x, y };
  };

  return (
    <table className="rec-tbl">
      <thead>
        <tr className="rec-tbl-hdr-row">
          {!isSmallScreen && <th className="rec-tbl-hdr-ctry"></th>}
          {!isSmallScreen && <th className="rec-tbl-hdr-ticker">Code</th>}
          {!isSmallScreen && <th className="rec-tbl-hdr-name">Name</th>}
          {isSmallScreen && (
            <th className="rec-tbl-hdr-code-name">Code / Name</th>
          )}
          {!isSmallScreen && <th className="rec-tbl-hdr-price">Last price</th>}
          {!isSmallScreen && <th className="rec-tbl-hdr-pct">%Chg</th>}
          <th className="rec-tbl-hdr-rec">Rating</th>
          {!isSmallScreen && <th className="rec-tbl-hdr-date">Updated</th>}
        </tr>
      </thead>

      <div className="rec-tbl-spacer"></div>

      <tbody>
        {!isLoading &&
          data &&
          data.map((item, index) => (
            <tr key={index} className="rec-tbl-cell-row">
              {!isSmallScreen && (
                <td className="rec-tbl-cell rec-tbl-cell-ctry">
                  {getFlagSvg(item.cc)}
                </td>
              )}
              {!isSmallScreen && (
                <td className="rec-tbl-cell rec-tbl-cell-ticker">
                  <span
                    className={onStockClick ? 'rec-tbl-ticker-link' : ''}
                    onClick={
                      onStockClick
                        ? () => onStockClick(item.bell_code)
                        : undefined
                    }
                  >
                    {item.ticker}
                  </span>
                </td>
              )}
              {!isSmallScreen && (
                <td className="rec-tbl-cell rec-tbl-cell-name">
                  <span
                    className={onStockClick ? 'rec-tbl-name' : ''}
                    onClick={
                      onStockClick
                        ? () => onStockClick(item.bell_code)
                        : undefined
                    }
                    onMouseEnter={(e) => {
                      const constrainedPosition = constrainTooltipPosition(e);
                      setTooltip({
                        show: true,
                        x: constrainedPosition.x,
                        y: constrainedPosition.y,
                        content: item.name,
                      });
                    }}
                    onMouseMove={(e) => {
                      //if no move, do not update tooltip
                      if (e.movementX === 0 && e.movementY === 0) {
                        return;
                      }
                      const constrainedPosition = constrainTooltipPosition(e);
                      setTooltip((prev) => ({
                        ...prev,
                        x: constrainedPosition.x,
                        y: constrainedPosition.y,
                      }));
                    }}
                    onMouseLeave={() => {
                      setTooltip((prev) => ({ ...prev, show: false }));
                    }}
                    onMouseOut={() => {
                      setTooltip((prev) => ({ ...prev, show: false }));
                    }}
                  >
                    {item.name}
                  </span>
                </td>
              )}
              {isSmallScreen && (
                <td className="rec-tbl-cell rec-tbl-cell-code-name">
                  <div className="rec-tbl-code-name-container">
                    <div className="rec-tbl-code-row">
                      {getFlagSvg(item.cc)}
                      <span
                        className={onStockClick ? 'rec-tbl-ticker-link' : ''}
                        onClick={
                          onStockClick
                            ? () => onStockClick(item.bell_code)
                            : undefined
                        }
                      >
                        {item.ticker}
                      </span>
                    </div>
                    <div className="rec-tbl-company-name-row">
                      <div className="rec-tbl-flag-placeholder"></div>
                      <div
                        className="rec-tbl-company-name"
                        onMouseEnter={(e) => {
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip({
                            show: true,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                            content: item.name,
                          });
                        }}
                        onMouseMove={(e) => {
                          if (e.movementX === 0 && e.movementY === 0) {
                            return;
                          }
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip((prev) => ({
                            ...prev,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                          }));
                        }}
                        onMouseLeave={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                        onMouseOut={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                      >
                        {item.name}
                      </div>
                    </div>
                  </div>
                </td>
              )}
              {!isSmallScreen && (
                <td className="rec-tbl-cell rec-tbl-cell-price">
                  {(() => {
                    return formatNumber(item.last.toString(), item.dp, item.dp);
                  })()}
                </td>
              )}
              {!isSmallScreen && (
                <td
                  className={classNames('rec-tbl-cell rec-tbl-cell-pct', {
                    positive: Number(item.pctchng) > 0,
                    negative: Number(item.pctchng) < 0,
                  })}
                >
                  {formatNumber(
                    item.pctchng.toString(),
                    2,
                    2,
                    false,
                    true,
                    false,
                    '%',
                  )}
                </td>
              )}
              <td className="rec-tbl-cell rec-tbl-cell-rec">
                <span
                  className={`rec-tbl-badge ${getRecommendationClass(item.rcmdlabel)}`}
                >
                  {item.rcmdlabel}
                </span>
              </td>
              {!isSmallScreen && (
                <td className="rec-tbl-cell rec-tbl-cell-date">{item.date}</td>
              )}
            </tr>
          ))}
      </tbody>
      <CommonTooltip
        ref={tooltipRef}
        content={tooltip.content}
        show={tooltip.show}
        x={tooltip.x}
        y={tooltip.y}
      />
    </table>
  );
};
export default RecommendationTable;
